class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    // A product-page Add to bag crossed a milestone while the drawer was closed: celebrate it now.
    if (Number.isFinite(this.pendingCelebrateFrom)) {
      const celebrateFrom = this.pendingCelebrateFrom;
      this.pendingCelebrateFrom = null;
      setTimeout(() => document.dispatchEvent(new CustomEvent('cart:updated', { detail: { celebrateFrom } })), 350);
    }
  }

  // Reward level the drawer shows right now (before new contents replace it), so any add can celebrate.
  currentRewardLevel() {
    const box = this.querySelector('[data-cart-rewards]');
    if (!box || this.classList.contains('is-empty')) return 0;
    const total = Number(box.dataset.progressTotal !== undefined ? box.dataset.progressTotal : box.dataset.cartTotal) || 0;
    const applied = box.dataset.tier1Applied === 'true';
    return (box.dataset.thresholds || '').split(',').map(Number).filter((t, i) => (i === 0 && applied) || (t > 0 && total >= t)).length;
  }

  close() {
    this.classList.remove('active');
    // Closing the drawer also closes a milestone popup, so it doesn't greet the customer again on reopen.
    const celebration = document.getElementById('milestone-celebration');
    if (celebration) {
      celebration.classList.remove('is-active');
      celebration.setAttribute('aria-hidden', 'true');
    }
    this.pendingCelebrateFrom = null;
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    // Something was just added, so the drawer is no longer empty. The <cart-drawer> element keeps
    // the server-rendered is-empty class otherwise, which hides the footer, coupon and upsells via CSS.
    this.classList.remove('is-empty');
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    this.productId = parsedState.id;
    const levelBefore = Number.isFinite(parsedState._rewardLevelBefore) ? parsedState._rewardLevelBefore : this.currentRewardLevel();
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Product page Add to bag passes { open: false }: the drawer is refreshed but stays closed.
      const stayClosed = options.open === false;
      if (stayClosed && !Number.isFinite(this.pendingCelebrateFrom)) this.pendingCelebrateFrom = levelBefore;
      if (!stayClosed) this.open();
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { celebrateFrom: stayClosed ? null : levelBefore } })
      );
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
